/**
 * AdminPage.tsx — staff admin shell.
 *
 * Phase-3 ready: tab bar with Partners (active) + Activity (coming soon).
 * Does NOT re-implement a staff check — the route is already wrapped in
 * RequireStaff by the integrator. Reads useAuth() only for display.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SiteHeader from './SiteHeader';
import AdminPartners from './AdminPartners';
import AdminActivity from './AdminActivity';

type TabId = 'partners' | 'activity';

interface Tab {
  id: TabId;
  label: string;
  disabled?: boolean;
  disabledLabel?: string;
}

const TABS: Tab[] = [
  { id: 'partners', label: 'Partners' },
  { id: 'activity', label: 'Activity' },
];

export default function AdminPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('partners');

  return (
    <div className="min-h-screen bg-paper-50">
      <SiteHeader />

      <main className="admin">
        {/* Page header */}
        <header className="admin__header">
          <div className="admin__header-inner">
            <div className="admin__header-title-row">
              <ShieldCheck className="admin__header-icon" aria-hidden="true" />
              <h1 className="admin__title">Admin</h1>
            </div>
            <div className="admin__header-meta">
              {user?.email && (
                <span className="admin__signed-in">
                  Signed in as <strong>{user.email}</strong>
                </span>
              )}
              <Link to="/" className="admin__back-link">
                ← Back to documentation
              </Link>
            </div>
          </div>
        </header>

        {/* Tab bar */}
        <div className="admin__tabs" role="tablist" aria-label="Admin sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={!tab.disabled && activeTab === tab.id}
              aria-controls={`admin-panel-${tab.id}`}
              id={`admin-tab-${tab.id}`}
              className={[
                'admin__tab',
                !tab.disabled && activeTab === tab.id ? 'admin__tab--active' : '',
                tab.disabled ? 'admin__tab--disabled' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={tab.disabled}
              onClick={() => {
                if (!tab.disabled) setActiveTab(tab.id);
              }}
              title={tab.disabled ? tab.disabledLabel : undefined}
            >
              {tab.label}
              {tab.disabled && tab.disabledLabel && (
                <span className="admin__tab-badge">{tab.disabledLabel}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab panels */}
        <div className="admin__body">
          <div
            id="admin-panel-partners"
            role="tabpanel"
            aria-labelledby="admin-tab-partners"
            hidden={activeTab !== 'partners'}
          >
            {activeTab === 'partners' && <AdminPartners />}
          </div>

          <div
            id="admin-panel-activity"
            role="tabpanel"
            aria-labelledby="admin-tab-activity"
            hidden={activeTab !== 'activity'}
          >
            {activeTab === 'activity' && <AdminActivity />}
          </div>
        </div>
      </main>
    </div>
  );
}
